import { PopupDialog } from "../lib/popup-dialog.ts";

interface MarketPromptRefs {
  overlay?: HTMLElement;

  dialog?: HTMLElement;

  message?: HTMLParagraphElement;

  countryInput?: HTMLInputElement;

  switch?: HTMLButtonElement;

  stay?: HTMLButtonElement;

  close?: HTMLButtonElement;

  currencies?: HTMLScriptElement;
}

interface DetectedCountry {
  name: string;
  handle: string;
  currency: string | null;
}

const DISMISSED_KEY = "market-prompt-dismissed";
const DETECTION_CACHE_PREFIX = "market-prompt-detection:";
const FORCE_PARAM = "force-market-prompt";

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object";
}

class MarketPrompt extends PopupDialog<MarketPromptRefs> {
  get inertId(): string {
    return "market-prompt";
  }

  getFocusTarget(): HTMLElement | null {
    return this.refs.stay ?? null;
  }

  onEscape(): void {
    this.#dismissForSession();
  }

  async connectedCallback(): Promise<void> {
    super.connectedCallback();

    const inDesignMode = window.Shopify?.designMode === true;
    const forced = new URLSearchParams(window.location.search).has(FORCE_PARAM);

    if (inDesignMode) {
      document.addEventListener("shopify:section:select", this.#handleSectionSelect, {
        signal: this.signal,
      });
      document.addEventListener("shopify:section:deselect", this.#handleSectionDeselect, {
        signal: this.signal,
      });
    } else if (!forced) {
      try {
        if (sessionStorage.getItem(DISMISSED_KEY)) return;
      } catch {
        return;
      }
    }

    const shopify = window.Shopify;
    if (!shopify) return;

    const usePreview = inDesignMode || forced;
    const country = usePreview ? this.#previewCountry() : await this.#detectedCountry(shopify);
    if (!country || !this.isConnected) return;

    const messageTemplate = country.currency
      ? (this.dataset.message ?? "")
      : (this.dataset.messageNoCurrency ?? "");
    const switchTemplate = this.dataset.switchLabel ?? "";
    const stayLabel = this.dataset.stayLabel ?? "";

    let message = messageTemplate.replace("__COUNTRY__", country.name);
    if (country.currency) message = message.replace("__CURRENCY__", country.currency);

    const { message: messageEl, switch: switchEl, stay, countryInput, overlay, close } = this.refs;
    if (!messageEl || !switchEl || !stay || !countryInput || !close) return;

    messageEl.textContent = message;
    switchEl.textContent = switchTemplate.replace("__COUNTRY__", country.name);
    stay.textContent = stayLabel;
    countryInput.value = country.handle;

    overlay.addEventListener("click", this.#handleStay, { signal: this.signal });
    close.addEventListener("click", this.#handleClose, { signal: this.signal });
    stay.addEventListener("click", this.#handleStay, { signal: this.signal });
    switchEl.form?.addEventListener("submit", this.#handleSwitch, {
      signal: this.signal,
    });

    if (!inDesignMode) this.open();
  }

  #handleSectionSelect = (event: Event): void => {
    if (event.target instanceof Element && event.target.contains(this)) {
      this.open();
    }
  };

  #handleSectionDeselect = (event: Event): void => {
    if (event.target instanceof Element && event.target.contains(this)) {
      this.close();
    }
  };

  #handleSwitch = (): void => {
    this.#persistDismissal();

    const form = this.refs.switch?.form;
    const returnTo = form?.querySelector('input[name="return_to"]');
    if (!(returnTo instanceof HTMLInputElement)) return;
    try {
      const url = new URL(returnTo.value, window.location.origin);
      url.searchParams.delete(FORCE_PARAM);
      returnTo.value = url.pathname + url.search + url.hash;
    } catch {
      // Malformed return_to — leave it for Shopify to resolve
    }
  };

  #handleStay = (): void => {
    this.#dismissForSession();
  };

  #handleClose = (): void => {
    this.#dismissForSession();
  };

  #dismissForSession(): void {
    this.#persistDismissal();
    this.close();
  }

  #persistDismissal(): void {
    if (window.Shopify?.designMode === true) return;
    try {
      sessionStorage.setItem(DISMISSED_KEY, "true");
    } catch {
      // Storage unavailable — prompt may reappear next page load
    }
  }

  async #detectedCountry(shopify: ShopifyGlobal): Promise<DetectedCountry | null> {
    const cacheKey = `${DETECTION_CACHE_PREFIX}${shopify.country}`;
    const cached = this.#readCache(cacheKey);
    if (cached !== undefined) return cached;

    const params = new URLSearchParams({
      "country[enabled]": "true",
      "country[exclude]": shopify.country,
    });

    let result: DetectedCountry | null = null;
    try {
      const response = await fetch(
        `${shopify.routes.root}browsing_context_suggestions.json?${params}`,
      );
      const data: unknown = await response.json();
      const suggested = this.#extractSuggestedCountry(data);
      if (suggested) {
        result = {
          name: suggested.name,
          handle: suggested.handle,
          currency: this.#currencyFor(suggested.handle),
        };
      }
    } catch {
      // Network or parsing error — cache as null so we don't retry this session
    }

    this.#writeCache(cacheKey, result);
    return result;
  }

  #extractSuggestedCountry(data: unknown): { name: string; handle: string } | null {
    if (!isRecord(data)) return null;
    const suggestions = data.suggestions;
    if (!Array.isArray(suggestions) || suggestions.length === 0) return null;
    const first: unknown = suggestions[0];
    if (!isRecord(first)) return null;
    const parts = first.parts;
    if (!isRecord(parts)) return null;
    const country = parts.country;
    if (!isRecord(country)) return null;
    const { name, handle } = country;
    if (typeof name !== "string" || typeof handle !== "string") return null;
    return { name, handle };
  }

  #readCache(key: string): DetectedCountry | null | undefined {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw === null) return undefined;
      const parsed: unknown = JSON.parse(raw);
      if (parsed === null) return null;
      if (!isRecord(parsed)) return undefined;
      const { name, handle, currency } = parsed;
      if (typeof name !== "string" || typeof handle !== "string") return undefined;
      return {
        name,
        handle,
        currency: typeof currency === "string" ? currency : null,
      };
    } catch {
      return undefined;
    }
  }

  #writeCache(key: string, value: DetectedCountry | null): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage unavailable — skip caching
    }
  }

  #currencyFor(handle: string): string | null {
    try {
      const raw: unknown = JSON.parse(this.refs.currencies?.textContent ?? "{}");
      if (!isRecord(raw)) return null;
      const value = raw[handle];
      return typeof value === "string" ? value : null;
    } catch {
      return null;
    }
  }

  #previewCountry(): DetectedCountry | null {
    const name = this.dataset.previewName;
    const handle = this.dataset.previewHandle;
    const currency = this.dataset.previewCurrency;
    if (!name || !handle || !currency) return null;
    return { name, handle, currency };
  }
}

if (!customElements.get("market-prompt")) {
  customElements.define("market-prompt", MarketPrompt);
}
